import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "../context/AuthContext";
import { authLogin, authRegister, errText } from "../lib/api";
import logoMark from "../assets/logo-mark.png";

export default function Auth({ mode = "login" }) {
  const [isLogin, setIsLogin] = useState(mode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/studio";

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !agreed) {
      setErr("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const u = isLogin
        ? await authLogin({ email, password })
        : await authRegister({ email, password, name });
      setUser(u);
      navigate(dest, { replace: true });
    } catch (e2) {
      setErr(errText(e2));
    }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md" data-testid="auth-card">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <img src={logoMark} alt="Faceless Studio" className="h-11 w-11 object-contain" style={{ filter: "drop-shadow(0 0 12px rgba(255,69,58,0.45))" }} />
          <span className="font-display font-extrabold text-xl">Faceless <span className="text-[#FF6A5E]">Studio</span></span>
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#11141E] p-7">
          <h1 className="font-display font-bold text-2xl text-center">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-slate-400 text-center mt-1">{isLogin ? "Sign in to keep creating ads" : "Start with 1 free video a week"}</p>

          <button data-testid="google-login-btn" onClick={google} type="button"
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white text-[#0B0C10] font-semibold py-2.5 hover:bg-slate-100 transition-colors">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {!isLogin && (
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="pl-9 bg-[#0B0C10] border-white/10 h-11" />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input data-testid="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="pl-9 bg-[#0B0C10] border-white/10 h-11" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input data-testid="auth-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="pl-9 bg-[#0B0C10] border-white/10 h-11" />
            </div>
            {err && <p data-testid="auth-error" className="text-sm text-red-400">{err}</p>}
            {!isLogin && (
              <label data-testid="auth-consent-label" className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
                <input
                  data-testid="auth-consent-checkbox"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-[#0B0C10] accent-[#FF453A] cursor-pointer"
                />
                <span className="text-xs text-slate-400 leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-[#FF6A5E] hover:underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" className="text-[#FF6A5E] hover:underline">Privacy Policy</Link>.
                </span>
              </label>
            )}
            <Button data-testid="auth-submit" type="submit" disabled={busy || (!isLogin && !agreed)} className="w-full bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full h-11 font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isLogin ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-slate-400 text-center mt-5">
            {isLogin ? "New here?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle" onClick={() => { setIsLogin(!isLogin); setErr(""); }} className="text-[#FF6A5E] font-semibold hover:underline">
              {isLogin ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
        <p className="text-center text-xs text-slate-500 mt-5">
          <Link to="/" className="hover:text-slate-300">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
